import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import labExtractionService, { type ExtractionJob } from '@/services/labExtractionService';
import { FileText, AlertCircle, CheckCircle2, XCircle, Clock, Search, RefreshCw } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const LabExtractionReviewPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState<ExtractionJob[]>([]);
  const [statusFilter, setStatusFilter] = useState('review_pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 20;

  useEffect(() => {
    fetchJobs();
  }, [statusFilter, page]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await labExtractionService.getExtractionJobs({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        page,
        limit,
      });
      setJobs(response.jobs);
      setTotalPages(response.pagination.totalPages);
      setTotalCount(response.pagination.totalCount);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to load extraction jobs',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: ExtractionJob['status']) => {
    const variants = {
      pending: { variant: 'secondary' as const, label: 'Pending', icon: Clock },
      processing: { variant: 'default' as const, label: 'Processing', icon: RefreshCw },
      extracted: { variant: 'default' as const, label: 'Extracted', icon: FileText },
      review_pending: { variant: 'outline' as const, label: 'Needs Review', icon: AlertCircle },
      approved: { variant: 'default' as const, label: 'Approved', icon: CheckCircle2 },
      rejected: { variant: 'destructive' as const, label: 'Rejected', icon: XCircle },
      failed: { variant: 'destructive' as const, label: 'Failed', icon: XCircle },
    };
    const config = variants[status] || variants.pending;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getConfidenceBadge = (confidence: number | null) => {
    if (!confidence) return null;
    const percentage = Math.round(confidence * 100);
    let variant: 'default' | 'secondary' | 'destructive' = 'default';
    let color = 'bg-emerald-500';
    
    if (confidence >= 0.8) {
      variant = 'default';
      color = 'bg-emerald-500';
    } else if (confidence >= 0.6) {
      variant = 'secondary';
      color = 'bg-amber-500';
    } else {
      variant = 'destructive';
      color = 'bg-red-500';
    }

    return (
      <Badge variant={variant} className={`${color} text-white gap-1`}>
        <div className="w-2 h-2 rounded-full bg-white" />
        {percentage}%
      </Badge>
    );
  };

  const filteredJobs = searchTerm
    ? jobs.filter(job =>
        job.patient.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.patient.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.fileName?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : jobs;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Lab Upload Review</h1>
          <p className="text-slate-600 mt-1">Review and approve AI-extracted lab results</p>
        </div>
        <Button onClick={fetchJobs} variant="outline" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search patient name or file..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="review_pending">Needs Review</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="extracted">Extracted</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Jobs Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Extraction Jobs ({totalCount})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>No extraction jobs found</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Patient</TableHead>
                    <TableHead>File Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Confidence</TableHead>
                    <TableHead>Tests</TableHead>
                    <TableHead>Provider</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Uploaded</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredJobs.map((job) => (
                    <TableRow
                      key={job.id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => navigate(`/clinic/labs/extractions/${job.id}`)}
                    >
                      <TableCell className="font-medium">
                        {job.patient.firstName} {job.patient.lastName}
                      </TableCell>
                      <TableCell className="max-w-xs truncate" title={job.fileName}>
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
                          {job.fileName}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(job.status)}</TableCell>
                      <TableCell>
                        {job.extractionConfidence ? getConfidenceBadge(job.extractionConfidence) : '—'}
                      </TableCell>
                      <TableCell>
                        {job.extractionResult?.tests.length || 0}
                      </TableCell>
                      <TableCell className="max-w-xs truncate">
                        {job.detectedProvider || '—'}
                      </TableCell>
                      <TableCell>
                        {job.detectedCollectionDate
                          ? format(parseISO(job.detectedCollectionDate), 'MMM dd, yyyy')
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {format(parseISO(job.createdAt), 'MMM dd, h:mm a')}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm">
                          Review →
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-slate-600">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LabExtractionReviewPage;
